// Tiny data-fetching hook with an in-memory cache (TanStack Query can replace it later
// with the same call sites: useQuery(key, fn) -> { data, loading, error, reload }).
import { useCallback, useEffect, useRef, useState } from 'react';

const cache = new Map<string, unknown>();

export function useQuery<T>(key: string, fn: () => Promise<T>) {
  const [data, setData] = useState<T | undefined>(() => cache.get(key) as T | undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!cache.has(key));
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const reload = useCallback(() => {
    let alive = true;
    setLoading((l) => l || !cache.has(key));
    fnRef.current()
      .then((d) => { if (!alive) return; cache.set(key, d); setData(d); setError(null); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [key]);

  useEffect(() => {
    setData(cache.get(key) as T | undefined);
    return reload();
  }, [key, reload]);

  return { data, error, loading, reload };
}

export function clearQueryCache(prefix?: string) {
  for (const k of [...cache.keys()]) if (!prefix || k.startsWith(prefix)) cache.delete(k);
}

/** Fill the cache ahead of time (e.g. the next episode). Returns the cached value if already there. */
export function prefetchQuery<T>(key: string, fn: () => Promise<T>): Promise<T | undefined> {
  if (cache.has(key)) return Promise.resolve(cache.get(key) as T);
  return fn().then((d) => { cache.set(key, d); return d; }).catch(() => undefined);
}
